import { useRef } from "react";
import newId from "../../../lib/id-utils.js";
import cx from "../../../lib/cx";
import Icon from "../Icon/Icon.jsx";
import styles from "./select.css";

/**
 * Renders a Select component with the provided options, selected value, label, and onChange handler.
 *
 * @param name
 * @param {Object[]} options - The options to be displayed in the select dropdown.
 * @param {string} options[].value - The value of the option.
 * @param {string} options[].label - The label of the option.
 * @param {string|string[]} selected - The currently selected value.
 * @param {string} [label="Auswählen"] - The label to be displayed above the select dropdown.
 * @param {function} onChange - The event handler for when the selected value changes.
 * @param required
 * @param error
 * @param {"light"|"dark"} theme
 * @returns {JSX.Element} - The rendered Select component.
 */
function Select({
  name,
  options,
  selected,
  label = "Auswählen",
  onChange,
  required,
  error,
  theme = "light",
}) {
  const id = useRef(newId());

  const labelClassName = selected
    ? `${styles.label} ${styles.labelOptionSelectedPosition[theme]}`
    : `${styles.label} ${styles.labelDefaultPosition[theme]}`;

  return (
    <div className={styles.selectGroup}>
      <div
        className={cx(
          styles.selectContainer[theme],
          error && styles.errorOutline,
        )}
      >
        <div className={styles.text}>
          <label className={labelClassName} htmlFor={id.current}>
            {label}
            {required && " *"}
          </label>

          <select
            name={name || id.current}
            id={id.current}
            className={styles.select[theme]}
            value={selected || undefined}
            onChange={onChange}
            multiple={false}
            required={required}
          >
            {options.map((option) => {
              if (option.value === "divider") {
                return <hr className={styles.divider} key={option.value} />;
              }

              return (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              );
            })}
          </select>
        </div>
        <div className={styles.iconContainer}>
          <Icon name={"chevron"} className={styles.rotate} size={"16px"} />
        </div>
      </div>
      {error && <p className={styles.error}>{error}</p>}
    </div>
  );
}

export default Select;
